import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { privacyPolicy, cookiePolicy, termsOfUse } from "@/data/legal";
import { openCookiePreferences } from "@/lib/consent";
import { CookieBanner } from "@/components/site/CookieBanner";

const docs = { privacy: privacyPolicy, cookie: cookiePolicy, terms: termsOfUse };
const nav = [["privacy", "/privacy", "Privacy Policy"], ["cookie", "/cookie-policy", "Cookie Policy"], ["terms", "/termini", "Termini di utilizzo"]];

const CookieTable = ({ rows }) => (
  <div className="overflow-x-auto -mx-5 sm:mx-0">
    <table className="w-full min-w-[720px] text-left border-t hairline" data-testid="cookie-table">
      <thead>
        <tr className="font-sans-ui text-[10px] tracking-mega text-ink/45">
          {["Categoria", "Nome", "Finalità", "Durata", "Fornitore"].map((h) => (
            <th key={h} className="py-3 pr-4 font-medium border-b hairline">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody className="font-sans-ui text-[13px] text-ink/75 leading-relaxed">
        {rows.map((r) => (
          <tr key={r[1] + r[2]} className="border-b hairline align-top">
            {r.map((c, j) => <td key={`${r[1]}-${j}`} className={`py-3 pr-4 ${j === 0 ? "text-ink font-medium whitespace-nowrap" : ""}`}>{c}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function Legal({ kind }) {
  const doc = docs[kind] || privacyPolicy;

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = `${doc.title} — My Brand Online`;
  }, [doc.title]);

  return (
    <div className="bg-ivory text-ink min-h-screen" data-testid={`legal-page-${kind}`}>
      <header className="border-b hairline px-5 sm:px-8 h-16 flex items-center justify-between sticky top-0 bg-ivory/90 backdrop-blur z-10">
        <Link to="/" className="flex items-center"><img src="/logo-mbo.png" alt="My Brand Online" className="h-5 w-auto" /></Link>
        <Link to="/" data-testid="legal-back-link" className="inline-flex items-center gap-2 font-sans-ui text-[10px] tracking-mega text-ink/55 hover:text-ink transition-colors">
          <ArrowLeft size={14} /> TORNA ALLA LANDING
        </Link>
      </header>

      <main className="max-w-[820px] mx-auto px-5 sm:px-8 py-16 sm:py-24">
        <p className="font-sans-ui text-[11px] tracking-mega text-ink/40 mb-6">LEGALE</p>
        <h1 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.0] tracking-tight mb-8">{doc.title}</h1>
        <p className="font-sans-ui text-base text-ink/65 leading-relaxed max-w-2xl">{doc.intro}</p>

        <div className="mt-16 space-y-14">
          {doc.sections.map((s) => (
            <section key={s.h}>
              <h2 className="font-display text-2xl sm:text-3xl text-ink mb-5">{s.h}</h2>
              {s.p?.map((t, i) => (
                <p key={i} className="font-sans-ui text-sm sm:text-[15px] text-ink/70 leading-relaxed mb-4 break-words">{t}</p>
              ))}
              {s.table && <CookieTable rows={s.table} />}
            </section>
          ))}
        </div>

        <div className="mt-20 border-t hairline pt-10 flex flex-col sm:flex-row gap-6 sm:items-center justify-between">
          <div className="flex flex-wrap gap-6 font-sans-ui text-sm">
            {nav.map(([k, to, label]) => (
              <Link key={k} to={to} data-testid={`legal-nav-${k}`} className={`hover:text-ink transition-colors ${kind === k ? "text-ink underline underline-offset-4" : "text-ink/55"}`}>{label}</Link>
            ))}
          </div>
          <button data-testid="legal-manage-cookies" onClick={openCookiePreferences} className="self-start bg-ink text-ivory px-6 py-3 text-[10px] tracking-mega font-medium hover:bg-charcoal transition-colors">
            GESTISCI COOKIE
          </button>
        </div>
      </main>
      <CookieBanner />
    </div>
  );
}
